/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import Ember from 'ember';
import { timeout } from 'ember-concurrency';
import { assert } from '@ember/debug';
import { task } from 'ember-concurrency';
import { isPresent } from '@ember/utils';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import AnalyticsService from 'cio/services/analytics';
import HibpService from 'cio/services/hibp';
import RouterService from '@ember/routing/router-service';
import UserPasswordValidations from 'cio/utils/validations/user-password';

const ACCEPTABLE_PWNED_COUNT = 1;
const DEBOUNCE_MS = 250;
const MINIMUM_USER_PASSWORD_SCORE = 3;
const MAXIMUM_USER_PASSWORD_SCORE = 4;
const VALIDATOR_FRUSTRATION_ALLOWANCE = 3000;

// TODO: consider using "zxcvbn-typescript" NPM package instead of adding own types here
type ZXCVBNResponse = {
  score: number;
  feedback: {
    warning: string;
    suggestions: string[];
  };
};

// mapping for which process the user is performing based on route
const PASSWORD_ENTRY_PROCESS_MAP: Record<string, string> = {
  'reset_password.new_password': 'Password Reset',
  'settings.personal.index': 'Password Edit',
  'signup.password': 'Signup',
};

interface PasswordValidatorArgs {
  password: string;
}

export default class PasswordValidator extends Component<PasswordValidatorArgs> {
  @service analytics!: AnalyticsService;
  @service hibp!: HibpService;
  @service router!: RouterService;

  ZXCVBNLoadAttempts = 0;

  // zxcvbn func mock used in unit tests: overwrites the real implementation
  zxcvbnForTests: (() => ZXCVBNResponse) | null = null;

  @alias('validatePassword.lastSuccessful.value.error') error!: string;

  @computed('router.currentRouteName')
  get passwordEnteredDuring() {
    return PASSWORD_ENTRY_PROCESS_MAP[this.router.currentRouteName];
  }

  @computed('validatePassword.lastSuccessful.value.isValid')
  get hasError() {
    let lastSuccessfulValidationTask = this.validatePassword.lastSuccessful;
    return isPresent(lastSuccessfulValidationTask) && !lastSuccessfulValidationTask?.value?.isValid;
  }

  trackPasswordRequirementFailure = task({ restartable: true }, async () => {
    // we cant wait for the track password task to complete when testing validation
    await timeout(!Ember.testing ? VALIDATOR_FRUSTRATION_ALLOWANCE : 0);

    if (!this.hasError) {
      return;
    }

    let promise = this.analytics.track('user', 'password_requirements_failed', {
      error: this.error.substring(0, 70),
      errorDuring: this.passwordEnteredDuring || 'no process provided',
    });

    return promise;
  });

  // TODO: replace "any" with zxcvbn types (custom?)
  loadZXCVBN = task({ restartable: true }, async (): Promise<any> => {
    if (this.zxcvbnForTests) {
      return this.zxcvbnForTests;
    }

    try {
      let zxcvbn = await import('zxcvbn');
      return zxcvbn.default;
    } catch (e) {
      if (this.ZXCVBNLoadAttempts < 1) {
        this.ZXCVBNLoadAttempts += 1;
        return this.loadZXCVBN.perform();
      }

      return;
    }
  });

  validatePassword = task({ restartable: true }, async (password: string) => {
    // dynamic module import: to avoid loading huge password dictionary bundle unless needed
    // see https://github.com/dropbox/zxcvbn#script-load-latency
    let zxcvbn = await this.loadZXCVBN.perform();
    let error!: string;
    let validationObject: {
      isValid: boolean;
      error: string;
      passwordScore: undefined | number;
      suggestions: string[];
    } = {
      isValid: true,
      error: '',
      passwordScore: undefined,
      suggestions: [],
    };

    this.trackPasswordRequirementFailure.perform();

    let isPasswordValid: string | boolean = UserPasswordValidations.password.validator(password);
    if (typeof isPasswordValid === 'string') {
      error = isPasswordValid;
      validationObject = { ...validationObject, error, isValid: false, passwordScore: 0 };
    } else {
      if (!Ember.testing) {
        await timeout(DEBOUNCE_MS);
      }

      let pwnedCount = await this.hibp.isPasswordCompromised(password);

      if (Number(pwnedCount) > ACCEPTABLE_PWNED_COUNT) {
        error = `Unfortunately, the password you've entered has been identified as compromised. Please choose another password.`;
        validationObject = { ...validationObject, error, isValid: false, passwordScore: 0 };
      } else if (zxcvbn) {
        let pwScoreObj = zxcvbn(password);
        validationObject.passwordScore = pwScoreObj.score;
        if (pwScoreObj.score < MINIMUM_USER_PASSWORD_SCORE) {
          error = pwScoreObj.feedback.warning || 'This password is guessable.';
          validationObject = {
            ...validationObject,
            error,
            isValid: false,
            passwordScore: pwScoreObj.score,
            suggestions: pwScoreObj.feedback.suggestions,
          };
        } else if (pwScoreObj.score < MAXIMUM_USER_PASSWORD_SCORE) {
          let { suggestions } = pwScoreObj.feedback;

          validationObject = {
            ...validationObject,
            passwordScore: pwScoreObj.score,
            suggestions:
              suggestions.length > 0 ? suggestions : ['Longer and more complex passwords are harder to guess.'],
          };
        }
      }
    }

    return validationObject;
  });

  constructor(owner: unknown, args: PasswordValidatorArgs) {
    super(owner, args);

    if (!Ember.testing) {
      assert(
        `PasswordValidator rendered at "${this.router.currentRouteName}" does not have a corresponding password entry task defined in the component`,
        Boolean(PASSWORD_ENTRY_PROCESS_MAP[this.router.currentRouteName]),
      );
    }

    let { password } = this.args;

    if (password != null && password !== '') {
      this.validatePassword.perform(password);
    }
  }
}
